import type { HTMLAttributes } from 'react';
import React, { useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { merge } from 'lodash';
import type { SchemaType } from '$util/schema';
import { TRUSTPILOT_TEMPLATE_ID } from '$constants/trustpilot';
import { ReviewProvider } from '$constants/review-providers';
import { TrustPilot } from '$quantum/atoms/TrustPilot';
import { DynamicRouteVrmLookup } from '$quantum/molecules/DynamicRouteVrmLookup';
import { Card } from '$quantum/atoms/Card';
import { CmsImageSchema, CmsNumberSchema, CmsShortTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { AllianzInsured } from '$quantum/molecules/AllianzInsured';
import { AskInHouseExperts } from '$quantum/molecules/AskInHouseExperts';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import type { ResponsiveImageProps } from '$quantum/atoms/ResponsiveImage';
import { ResponsiveImage } from '$quantum/atoms/ResponsiveImage';
import type { PageTypeProps, VrmLookupOnSubmitData } from '$commons/hooks/useVrmLookup';
import { useVrmLookup } from '$commons/hooks/useVrmLookup';
import type { AnyObject } from '$util/types';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { useTrustpilotHeight } from '$quantum/atoms/TrustPilot/useTrustPilotHeight';
import { AntGrid, Styled, StyledImageContainer } from './styled';

const { Row, Col } = AntGrid;

const defaultConfiguration = makeContentComponentDefaultConfig({
  reviewsProvider: ReviewProvider.TRUSTPILOT,
  sku: undefined,
});

export const VrmHeroSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    subheading: CmsShortTextSchema,
    postcodeLabel: CmsShortTextSchema,
    vrmLabel: CmsShortTextSchema,
    desktopImage: CmsImageSchema,
    mobileImage: CmsImageSchema.optional(),
    mainCta: CmsShortTextSchema.optional(),
    invalidPostcodeMessage: CmsShortTextSchema.optional(),
    invalidVRMMessage: CmsShortTextSchema.optional(),
    missingPostcodeMessage: CmsShortTextSchema.optional(),
    missingVRMMessage: CmsShortTextSchema.optional(),
    insuredText: CmsShortTextSchema.optional(),
    insuredLogo: CmsImageSchema.optional(),
    askExpertsText: CmsShortTextSchema.optional(),
    askExpertsLinkText: CmsShortTextSchema.optional(),
    askExpertsAnchorId: CmsShortTextSchema.optional(),
    tagText: CmsShortTextSchema.optional(),
    googleReviewsStars: CmsNumberSchema.optional(),
    googleReviewsTotalReviews: CmsNumberSchema.optional(),
    googleReviewsLink: CmsShortTextSchema.optional(),
    googleReviewsReviewsText: CmsShortTextSchema.optional(),
    googleReviewsBasedOnText: CmsShortTextSchema.optional(),
    googleReviewsLogo: CmsImageSchema.optional(),
    imageTextSaving: CmsShortTextSchema.optional(),
    imageTextOperation: CmsShortTextSchema.optional(),
    imageTextFirstName: CmsShortTextSchema.optional(),
    imageTextVehicle: CmsShortTextSchema.optional(),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
      reviewsProvider: z.nativeEnum(ReviewProvider).optional(),
      sku: z.string().optional(),
      preloadImages: z.boolean().optional(),
      lazyLoadImages: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type VrmHeroTranslatedEntities = SchemaType<(typeof VrmHeroSchema)['translatedEntities']>;
export type VrmHeroConfiguration = SchemaType<(typeof VrmHeroSchema)['configuration']>;

export interface VrmHeroProps extends HTMLAttributes<HTMLDivElement> {
  readonly pageType?: PageTypeProps[];
  readonly sku?: string;
  readonly extraParams?: AnyObject;
  readonly targetBlank?: boolean;
  readonly formAction?: string;
}

/**
 * Component that renders a full width Hero image and Vrm lookup.<br />
 * https://www.notion.so/fixter/VrmHero-b59a834a931d4e9bac7de8cdbd13f965
 */
export const VrmHero: ContentComponent<VrmHeroTranslatedEntities, VrmHeroConfiguration, VrmHeroProps> =
  function VrmHero({
    translatedEntities,
    configuration,
    className,
    pageType,
    extraParams,
    targetBlank,
    formAction = '/vehicles/add',
  }): JSX.Element {
    const cmsPageContext = useCmsPageContext();
    const {
      heading,
      desktopImage,
      subheading,
      mobileImage,
      mainCta,
      invalidPostcodeMessage,
      invalidVRMMessage,
      missingPostcodeMessage,
      missingVRMMessage,
      postcodeLabel,
      vrmLabel,
      insuredText,
      insuredLogo,
      askExpertsText,
      askExpertsLinkText,
      askExpertsAnchorId,
      tagText,
      googleReviewsStars,
      googleReviewsTotalReviews,
      googleReviewsLink,
      googleReviewsReviewsText,
      googleReviewsBasedOnText,
      googleReviewsLogo,
      imageTextSaving,
      imageTextOperation,
      imageTextFirstName,
      imageTextVehicle,
    } = translatedEntities;
    const { reviewsProvider, sku, preloadImages, lazyLoadImages } = merge(
      defaultConfiguration,
      configuration
    );

    const images: ResponsiveImageProps[] = useMemo(() => {
      const imageProps: ResponsiveImageProps[] = [
        {
          src: desktopImage.url,
          alt: desktopImage.alt,
          fromBreakpoint: 'md',
        },
      ];

      if (mobileImage) {
        imageProps.push({
          src: mobileImage.url,
          alt: mobileImage.alt,
          toBreakpoint: 'md',
        });
      }

      return imageProps;
    }, [mobileImage, desktopImage]);

    const [desktopViewport, setDesktopViewport] = useState(false);

    const imageTextBlock = Boolean(
      imageTextSaving?.value ||
        imageTextOperation?.value ||
        (imageTextFirstName?.value && desktopViewport) ||
        (imageTextVehicle?.value && desktopViewport)
    );

    const [{ vrm, postcode }, setState] = useState({ vrm: '', postcode: '' });

    const isDesktopViewport = useCallback((isDesktop: boolean) => {
      setDesktopViewport(isDesktop);
    }, []);

    useResizeViewport({ breakpoint: 'lg', direction: 'up', fn: isDesktopViewport });

    const cmsContextSku = cmsPageContext?.pageRoutes?.[0]?.collectionItems?.product?.sku;
    const lookupSkus = sku || cmsContextSku;
    const { onSubmit, segment } = useVrmLookup({
      extraParams,
      formAction,
      pageType,
      sku: lookupSkus,
      targetBlank,
    });

    const trustpilotHeight = useTrustpilotHeight();

    return (
      <Styled className={classNames(VrmHero.displayName, className)} $reviewsProvider={reviewsProvider}>
        <div className="container">
          <Row>
            <Col xs={12} md={6} lg={5} className="vrm">
              <Card className="vrm">
                <div className="headings">
                  <HeadingLevels className="Heading" semanticLevel={1}>
                    {heading.value}
                  </HeadingLevels>
                  {subheading && <p className="subheading">{subheading.value}</p>}
                  <DynamicRouteVrmLookup
                    postcodeField={{
                      label: postcodeLabel?.value,
                      value: postcode,
                      invalidMessage: invalidPostcodeMessage?.value,
                      missingMessage: missingPostcodeMessage?.value,
                    }}
                    vrmField={{
                      label: vrmLabel?.value,
                      value: vrm,
                      invalidMessage: invalidVRMMessage?.value,
                      missingMessage: missingVRMMessage?.value,
                    }}
                    mainCta={mainCta?.value}
                    tagText={tagText?.value}
                    changeValueFunction={setState}
                    onSubmitForm={(data) => onSubmit(data as VrmLookupOnSubmitData)}
                    segment={segment}
                  />
                </div>
                <div className="trust">
                  <div>
                    {reviewsProvider === ReviewProvider.TRUSTPILOT ? (
                      <TrustPilot
                        templateId={TRUSTPILOT_TEMPLATE_ID}
                        height={trustpilotHeight}
                        width="100%"
                      />
                    ) : (
                      googleReviewsStars &&
                      googleReviewsTotalReviews &&
                      googleReviewsLink && (
                        <GoogleReviewsSummary
                          rating={googleReviewsStars?.number}
                          totalReviews={googleReviewsTotalReviews?.number}
                          link={googleReviewsLink?.value}
                          reviewsText={googleReviewsReviewsText?.value}
                          basedOnText={googleReviewsBasedOnText?.value}
                          providerLogo={googleReviewsLogo}
                          providerLogoWidth={66}
                          providerLogoHeight={24}
                        />
                      )
                    )}
                  </div>
                  {insuredLogo && (
                    <div>
                      <AllianzInsured width={92} height={24} image={insuredLogo}>
                        {insuredText?.value}
                      </AllianzInsured>
                    </div>
                  )}
                </div>
                {askExpertsLinkText?.value && (
                  <div className="ask-experts">
                    <AskInHouseExperts
                      linkText={askExpertsLinkText?.value ?? ''}
                      anchorId={askExpertsAnchorId?.value}
                    >
                      {askExpertsText?.value ?? ''}
                    </AskInHouseExperts>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </div>

        <StyledImageContainer>
          {images.map(({ src, ...props }: ResponsiveImageProps) => (
            <ResponsiveImage key={src} src={src} {...props} preload={preloadImages} lazy={lazyLoadImages} />
          ))}
          {imageTextBlock && (
            <div className="image-text ant-col-md-6 ant-col-sm-12">
              {imageTextSaving?.value && <span className="saving">{imageTextSaving.value}</span>}
              {imageTextOperation?.value && <span className="operation">{imageTextOperation.value}</span>}
              {(imageTextFirstName?.value || imageTextVehicle?.value) && desktopViewport && (
                <span className="first-name">
                  {[imageTextFirstName?.value, imageTextVehicle?.value].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          )}
        </StyledImageContainer>
      </Styled>
    );
  };

VrmHero.displayName = 'VrmHero';
VrmHero.schema = VrmHeroSchema;
